<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Component } from 'vue';
import * as LucideIcons from 'lucide-vue-next';
import ModalContent from '@/components/ModalContent.vue';

type Palette = { soft: string; strong: string; hoverStrong: string };

const DEFAULT_PALETTE: Palette = {
  soft: 'rgba(4, 120, 87, 0.1)',
  strong: '#047857',
  hoverStrong: '#036a4b',
};

const COLOUR_MAP: Record<string, Palette> = {
  primary: DEFAULT_PALETTE,
  secondary: {
    soft: 'rgba(124, 58, 237, 0.1)',
    strong: '#7c3aed',
    hoverStrong: '#6d28d9',
  },
  tertiary: {
    soft: 'rgba(234, 88, 12, 0.1)',
    strong: '#ea580c',
    hoverStrong: '#c2410c',
  },
  quaternary: {
    soft: 'rgba(8, 145, 178, 0.1)',
    strong: '#0891b2',
    hoverStrong: '#0e7490',
  },
  quinary: {
    soft: 'rgba(217, 119, 6, 0.1)',
    strong: '#d97706',
    hoverStrong: '#b45309',
  },
};

const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;
    link_text?: string;
    colour?: string;
    modal_items?: string[];
  }>(),
  {
    title: '',
    description: '',
    link_text: 'Start',
    colour: 'primary',
    modal_items: () => [],
  }
);

const isOpen = ref(false);
const CircleCheckIcon =
  (LucideIcons as Record<string, Component>).CircleCheck ??
  (LucideIcons as Record<string, Component>).CircleCheckBig;

function resolvePalette(colourName?: string): Palette {
  const key = (colourName ?? '').trim();
  return COLOUR_MAP[key] ?? DEFAULT_PALETTE;
}

const palette = computed<Palette>(() => resolvePalette(props.colour));

const modalItems = computed(() =>
  Array.isArray(props.modal_items)
    ? props.modal_items
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0)
    : []
);

function openModal() {
  isOpen.value = true;
}

function closeModal() {
  isOpen.value = false;
}
</script>

<template>
  <section class="check-list-block">
    <div class="check-list-block__icon-wrap" :style="{ backgroundColor: palette.soft }">
      <component
        :is="CircleCheckIcon"
        class="check-list-block__icon"
        :style="{ color: palette.strong }"
        aria-hidden="true"
      />
    </div>

    <h3 class="check-list-block__title">{{ title }}</h3>
    <p class="check-list-block__description">{{ description }}</p>

    <button
      type="button"
      class="check-list-block__button"
      :style="{
        backgroundColor: palette.strong,
        '--check-list-button-hover': palette.hoverStrong,
      }"
      @click="openModal"
    >
      {{ link_text }}
    </button>

    <ModalContent
      :open="isOpen"
      :title="title"
      :description="description"
      :items="modalItems"
      @close="closeModal"
    />
  </section>
</template>

<style scoped>
.check-list-block {
  background-color: #fff;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  padding: 1rem;
  margin-bottom: 1rem;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.check-list-block:hover {
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  transform: translateY(-4px);
}

.check-list-block__icon-wrap {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.check-list-block__icon {
  width: 1.25rem;
  height: 1.25rem;
}

.check-list-block__title {
  margin: 0 0 0.5rem;
  color: #111827;
  font-size: 1rem;
  line-height: 1.4;
  font-weight: 600;
}

.check-list-block__description {
  margin: 0 0 1rem;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.55;
}

.check-list-block__button {
  width: 100%;
  border: 0;
  border-radius: 0.625rem;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  padding: 0.625rem 1rem;
  cursor: pointer;
  transition: transform 0.2s ease, filter 0.2s ease, background-color 0.2s ease;
}

.check-list-block__button:hover {
  background-color: var(--check-list-button-hover);
  transform: translateY(-1px);
}

.check-list-block__button:active {
  transform: translateY(0);
}

@media (min-width: 640px) {
  .check-list-block {
    border-radius: 1rem;
    padding: 1.5rem;
  }

  .check-list-block__icon-wrap {
    width: 3rem;
    height: 3rem;
    border-radius: 0.75rem;
    margin-bottom: 1rem;
  }

  .check-list-block__icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .check-list-block__title {
    font-size: 1.125rem;
  }

  .check-list-block__description {
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }

  .check-list-block__button {
    border-radius: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    font-size: 1rem;
  }
}
</style>
